import { useAppDispatch, useAppSelector } from '@/store';
import {
  selectAuthStatus,
  selectCurrentUser,
  selectMerchantId,
  selectStoreId,
  selectRoles,
  selectPermissions,
  selectAvailableMerchants,
  selectAvailableStores,
  selectIsAuthenticated,
  selectIsLegacySession,
  selectHasStore,
  selectSessionType,
  logoutUser,
} from '@/store/slices/authSlice';
import { useNavigate } from 'react-router-dom';
import { useCallback } from 'react';

/**
 * Primary auth hook for the application.
 * Provides auth state, user info, context, and logout function.
 *
 * Replaces the old useAuth that relied on js-cookie + jwt-decode.
 */
export const useAuth = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const status = useAppSelector(selectAuthStatus);
  const user = useAppSelector(selectCurrentUser);
  const merchantId = useAppSelector(selectMerchantId);
  const storeId = useAppSelector(selectStoreId);
  const roles = useAppSelector(selectRoles);
  const permissions = useAppSelector(selectPermissions);
  const availableMerchants = useAppSelector(selectAvailableMerchants);
  const availableStores = useAppSelector(selectAvailableStores);
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const isLegacySession = useAppSelector(selectIsLegacySession);
  const hasStore = useAppSelector(selectHasStore);
  const sessionType = useAppSelector(selectSessionType);

  const logout = useCallback(async () => {
    try {
      await dispatch(logoutUser()).unwrap();
    } catch (error) {
      console.error('Logout failed:', error);
    }
    navigate('/login', { replace: true });
  }, [dispatch, navigate]);

  return {
    // State
    status,
    user,
    isAuthenticated,
    isLegacySession,
    hasStore,
    sessionType,

    // Context
    merchantId,
    storeId,
    roles,
    permissions,

    // Available contexts (for store/merchant switching)
    availableMerchants,
    availableStores,

    // Actions
    logout,

    // Legacy compat fields
    loading: status === 'initializing',
    isNewStore: false, // No longer tracked in auth slice
    storeUrl: '', // No longer tracked in auth slice
  };
};

export default useAuth;
